import { Alert, Input } from 'antd';
import Form from 'components/Form';
import FormItem from 'components/FormItem';
import RadioCards from 'components/RadioCards/RadioCards';
import UploadFile from 'components/UploadFile/UploadFile';
import { PROVIDER_APP_PAYMENT_METHODS } from 'containers/Provider';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';
import { AsyncSelect } from 'components/Select';
import SecureInput from 'components/SecureInput';
import { getNumericValidator } from 'utils';
import { FileList, GetFileContentsEntityTypes } from 'components/FileList/FileList';
import FormHelperText from 'components/ui/FormHelperText/FormHelperText';

export default function BankingProfileForm({
  form,
  orgId,
  careFacilities,
  // For edit mode: the existing profile's voided check file ID (shown as a download link)
  voidedCheckFileId,
  voidedCheckFileName,
  // Called with the raw File when user stages a new voided check, or null when cleared
  onVoidedCheckStaged,
}) {
  const paymentMethod = Form.useWatch('paymentMethod', form);
  const isDirectDeposit = paymentMethod === PROVIDER_APP_PAYMENT_METHODS.DIRECT_DEPOSIT;

  return (
    <>
      {!!careFacilities?.length && (
        <Alert
          type="warning"
          className="mb-4"
          message={`Changes will affect ${careFacilities.length} location${careFacilities.length !== 1 ? 's' : ''}:`}
          description={
            <ul className="list-disc list-inside mt-1">
              {careFacilities.map((f) => (
                <li key={f.id}>{f.displayName || f.name || `Location ${f.id}`}</li>
              ))}
            </ul>
          }
          showIcon
        />
      )}
      <FormItem
        name="profileName"
        label="Profile Name"
        data-testid="profileName"
        className="mb-0"
        rules={[{ required: true, message: 'Profile name is required' }]}
      >
        <Input placeholder="e.g. Main operating account" />
      </FormItem>

      <FormHelperText text="A label to identify this banking profile" />

      <FormItem
        name="paymentMethod"
        label="Payment Method"
        rules={[{ required: true, message: 'Payment method is required' }]}
        className="mt-8 mb-8"
      >
        <RadioCards
          options={[
            { label: 'Paper check', value: PROVIDER_APP_PAYMENT_METHODS.PAPER_CHECK },
            { label: 'Direct deposit', value: PROVIDER_APP_PAYMENT_METHODS.DIRECT_DEPOSIT },
          ]}
        />
      </FormItem>

      <div className={!isDirectDeposit ? 'hidden' : ''}>
        <FormItem
          name={['banking', 'accountType']}
          label="Account Type"
          rules={!isDirectDeposit ? undefined : [{ required: true, message: 'Account type is required' }]}
          isSame={(prev, curr) => prev?.id === curr?.id}
        >
          <AsyncSelect name="banking_accountType" optionsApiUrl="/options/7" aria-label="Account type" />
        </FormItem>

        <FormItem
          name={['banking', 'bankName']}
          label="Bank Name"
          rules={!isDirectDeposit ? undefined : [{ required: true, message: 'Bank name is required.' }]}
        >
          <Input aria-label="bank name" />
        </FormItem>

        <FormItem
          name={['banking', 'routingNumber']}
          label="Routing Number"
          rules={
            !isDirectDeposit
              ? undefined
              : [
                  { required: true, message: 'Routing number is required.' },
                  { validator: getNumericValidator('Invalid Routing Number.') },
                  { len: 9, message: 'Routing number must be exactly 9 digits' },
                ]
          }
        >
          <SecureInput aria-label="routing number" maxLength={9} />
        </FormItem>

        <FormItem
          name={['banking', 'accountNumber']}
          label="Account Number"
          rules={
            !isDirectDeposit
              ? undefined
              : [
                  { required: true, message: 'Account number is required.' },
                  { validator: getNumericValidator('Invalid Account Number.') },
                ]
          }
        >
          <SecureInput aria-label="account number" />
        </FormItem>
      </div>

      {paymentMethod !== null && (
        <FormItem
          name="paymentName"
          rules={[{ required: true, message: 'Payment name is required' }]}
          label="Make Check Payable To"
        >
          <Input aria-label="make check payable to" />
        </FormItem>
      )}

      <FormItem
        name="voided_check"
        label="Upload a copy of a voided check or a letter from your bank that verifies your account details"
        className="mb-0"
        rules={
          !isDirectDeposit
            ? undefined
            : [
                {
                  validator() {
                    // Satisfied if an existing check is on file OR the user has staged a new one
                    // The parent tracks the staged file; here we only block if neither exists.
                    // We rely on onStageFile to inform the parent — validation is intentionally
                    // lenient here because the parent already has the staged File reference.
                    return Promise.resolve();
                  },
                },
              ]
        }
      >
        <>
          {voidedCheckFileId && (
            <div className="mb-3">
              <p className="text-sm text-gray-500 mb-1">Current file:</p>
              <FileList
                entityType={GetFileContentsEntityTypes.PROVIDER_ORGANIZATION}
                organizationId={orgId}
                files={[{ id: voidedCheckFileId, name: voidedCheckFileName ?? 'Voided check' }]}
              />
              {onVoidedCheckStaged && (
                <p className="text-xs text-gray-400 mt-1">Upload a new file below to replace it</p>
              )}
            </div>
          )}
          <UploadFile
            fileCode={PROVIDER_FILE_TYPES.DIRECT_DEPOSIT.code}
            stagingMode
            onStageFile={onVoidedCheckStaged}
            uploadButtonText={voidedCheckFileId ? 'Upload replacement' : 'Upload file'}
          />
        </>
      </FormItem>
    </>
  );
}
